import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Spinner from "../components/Spinner";

function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const team = location.state?.team;

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!team?.teamId) {
      setLoading(false);
      return;
    }
    fetch(`${process.env.REACT_APP_API_URL}/api/players/team/${team.teamId}`)
      .then((res) => res.json())
      .then((data) => setPlayers(data))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [team?.teamId]);

  const scrollTo = location.state?.scrollTo;

  useEffect(() => {
    if (!scrollTo || loading) return;
    document.getElementById(scrollTo)?.scrollIntoView({ behavior: "smooth" });
  }, [scrollTo, location.key, loading]);

  const backToTeams = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/teams/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: team.email, phone: team.phone }),
        },
      );
      const data = await res.json();
      if (res.ok) {
        navigate("/coach", { state: { teams: data } });
      } else {
        navigate("/");
      }
    } catch (err) {
      navigate("/");
    }
  };

  // Only paid teams can open a team dashboard
  if (!team || team.paymentStatus !== "PAID") {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <p className="text-lg">This team is not registered yet.</p>
          <button
            onClick={() => navigate("/")}
            className="bg-brand-red text-white font-bold px-4 py-2 rounded-lg hover:bg-red-800"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-brand-cream">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        team={team}
      />

      <div className="flex-1 p-6">
        <button
          onClick={() => setSidebarOpen(true)}
          className="md:hidden mb-4 bg-brand-dark text-white px-3 py-2 rounded-lg text-xl"
        >
          ☰
        </button>

        <button
          onClick={backToTeams}
          className="block mb-3 text-sm font-semibold text-brand-red underline"
        >
          ← My teams
        </button>

        <h1 className="text-2xl font-bold text-brand-red mb-4">
          Welcome, {team?.coachName || "Coach"}!
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-xs text-gray-500">Category</p>
            <p className="text-lg font-bold text-brand-dark">
              {team?.category}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-xs text-gray-500">Payment</p>
            <p className="text-lg font-bold text-green-600">
              {team?.paymentStatus}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-xs text-gray-500">Players</p>
            <p className="text-lg font-bold text-brand-dark">
              {players.length} / 25
            </p>
          </div>

          <div className="bg-white rounded-xl shadow p-4">
            <p className="text-xs text-gray-500">Verified</p>
            <p className="text-lg font-bold text-brand-dark">
              {
                players.filter((p) => p.verificationStatus === "VERIFIED")
                  .length
              }
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow p-5">
            <h2 className="text-lg font-bold text-brand-dark mb-3">
              Team Information
            </h2>
            <div className="space-y-1 text-sm">
              <p>
                <strong>Team ID:</strong> {team?.teamId}
              </p>
              <p>
                <strong>Team Name:</strong> {team?.teamName}
              </p>
              <p>
                <strong>County:</strong> {team?.county}
              </p>
              <p>
                <strong>Coach:</strong> {team?.coachName}
              </p>
              <p>
                <strong>Category:</strong> {team?.category}
              </p>
              <p>
                <strong>Payment Status:</strong> {team?.paymentStatus}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow p-5">
            <h2 className="text-lg font-bold text-brand-dark mb-3">
              Quick Actions
            </h2>
            <div className="space-y-2">
              <button
                className="w-full bg-brand-red text-white font-bold py-2.5 rounded-lg hover:bg-red-800 disabled:opacity-50"
                onClick={() => navigate("/add-player", { state: { team } })}
                disabled={players.length >= 25}
              >
                + Add Player
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById("players-section")
                    .scrollIntoView({ behavior: "smooth" })
                }
                className="w-full bg-brand-gold text-brand-dark font-bold py-2.5 rounded-lg hover:opacity-90"
              >
                View Players
              </button>
            </div>
          </div>
        </div>

        <div
          id="players-section"
          className="bg-white rounded-2xl shadow p-5 mt-4"
        >
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-bold text-brand-dark">
              Registered Players ({players.length} / 25)
            </h2>
            <button
              className="bg-brand-red text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-red-800 disabled:opacity-50"
              onClick={() => navigate("/add-player", { state: { team } })}
              disabled={players.length >= 25}
            >
              + Add Player
            </button>
          </div>

          {loading ? (
            <Spinner />
          ) : players.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">
              No players registered yet. Click "Add Player" to get started.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2">Name</th>
                  <th className="py-2">DOB</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Reg #</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => (
                  <tr key={p._id} className="border-b">
                    <td className="py-2">{p.fullName}</td>
                    <td className="py-2">
                      {new Date(p.dob).toLocaleDateString()}
                    </td>
                    <td className="py-2">{p.verificationStatus}</td>
                    <td className="py-2">{p.regNumber || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
